import { useMemo } from 'react';
import type { RuleClause } from '@/hooks/useNotifications.ts';
import type { TagDefinition } from '@/hooks/useTagDefinitions';
import { useSelectableTagDefinitions, useTagValueLabels } from '@/components/tags/tagSelection.ts';
import type { ScopeOption } from './ruleSentence.ts';

/**
 * Everything a clause can point at, for one organization.
 *
 * Shared by personal rules and organization routes because a clause is the same
 * object on both sides — including which tags are offered on which axis, which is
 * exactly the kind of rule that goes wrong quietly if two screens each decide it.
 */

/**
 * The scope axis reads a scope row's device tags **unioned with its region's and its
 * division's** (`services/notifications/tags.rs`), and reads nothing else. A tag valid
 * only for credentials would be offered here and could never match, so it is not
 * offered. Module constants because they are `useMemo` dependencies.
 */
const SCOPE_TAG_TYPES = ['device', 'region', 'division'];
const ALERT_TAG_TYPES = ['alert_definition'];

export interface ClauseTargets {
  scopes: ScopeOption[];
  tagDefinitions: TagDefinition[];
  tagValueLabels: Record<string, string>;
}

export function useClauseTargets(
  organizationId: string | undefined,
  divisions: { id: string; name: string }[],
  regions: { id: string; name: string }[],
  /** Every clause currently on screen, so their values can be named. */
  clauses: RuleClause[],
): ClauseTargets {
  const { definitions: scopeTags } = useSelectableTagDefinitions({
    organizationId,
    resourceTypes: SCOPE_TAG_TYPES,
  });
  const { definitions: alertTags } = useSelectableTagDefinitions({
    organizationId,
    resourceTypes: ALERT_TAG_TYPES,
  });

  const scopes = useMemo<ScopeOption[]>(
    () => [
      ...divisions.map((d) => ({ id: d.id, name: d.name, kind: 'division' as const })),
      ...regions.map((r) => ({ id: r.id, name: r.name, kind: 'region' as const })),
      ...scopeTags.map((t) => ({ id: t.id, name: t.key, kind: 'deviceTag' as const })),
      ...alertTags.map((t) => ({ id: t.id, name: t.key, kind: 'alertTag' as const })),
    ],
    [divisions, regions, scopeTags, alertTags],
  );

  // Deduped: a definition valid for devices *and* alert definitions belongs in both
  // groups — they ask different questions — but the chip looks it up by id and wants
  // one answer.
  const tagDefinitions = useMemo(() => {
    const byId = new Map<string, TagDefinition>();
    for (const definition of [...scopeTags, ...alertTags]) byId.set(definition.id, definition);
    return [...byId.values()];
  }, [scopeTags, alertTags]);

  // Only definitions a clause on screen actually pins a value from. Usually none, and
  // never more than the handful someone wrote down.
  const definitionsInUse = useMemo(
    () =>
      clauses
        .filter((c) => c.tagDefinitionId && c.tagValueId)
        .map((c) => c.tagDefinitionId)
        .filter((id): id is string => !!id),
    [clauses],
  );

  return { scopes, tagDefinitions, tagValueLabels: useTagValueLabels(organizationId, definitionsInUse) };
}
